<template>
  <Teleport to="body">
    <div class="set-action-overlay" @click="close">
      <div class="set-action-container" @click.stop>
        <!-- 关闭按钮 -->
        <div class="mgx_close_btn close_btn_pos" @click="close">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>

        <!-- 动态标题 -->
        <h2 class="title">{{ demoConfig.title }}（{{ demoConfig.typeLabel }}）{{ $t('demo') }}</h2>

        <div class="content">
          <!-- 左侧内容区 -->
          <div class="left-section">
            <!-- 功能原理区 -->
            <div class="function-principle">
              <div class="section-title">{{ $t('function_principle') }}</div>
              <div class="principle">{{ demoConfig.principle }}</div>
            </div>

            <!-- 核心特性 -->
            <div class="core-features">
              <div class="section-title">{{ $t('core_features') }}</div>
              <ul class="feature-list">
                <li v-for="feature in demoConfig.features" :key="feature">
                  {{ feature }}
                </li>
              </ul>
            </div>

            <!-- 说明文字 -->
            <div class="description">
              <p v-for="desc in demoConfig.description" :key="desc">
                {{ desc }}
              </p>
            </div>
          </div>

          <!-- 右侧演示区 -->
          <div class="demo-section">
            <div class="function-options" v-if="demoConfig.tabs">
              <div class="tabs" v-if="type == SetType.kme ? isKMEModeSupported : true">
                <button v-for="tab in demoConfig.tabs" :key="tab.value" class="tab" :class="{ active: activeTab === tab.value }" @click="activeTab = tab.value">
                  {{ tab.label }}
                </button>
              </div>
            </div>

            <div class="demo-container">
              <div class="video-demo-area">
                <img v-if="currentVideoSrc" :src="currentVideoSrc" class="demo-image" alt="演示图片" />
                <!-- 占位符 -->
                <div v-else class="demo-placeholder">
                  <div class="placeholder-icon">🎬</div>
                  <div class="placeholder-text">演示内容</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="footer">
          <button class="confirm-btn" @click="confirm">{{ $t('i_know') }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { Brand, getBrand } from '@/brand/brand'
import i18n from '@/languages/i18n'
import { DeviceCapability } from '@/service/device_capability'
import { SetType } from '@/service/driver/action_set'
import { isElectron } from '@libs/utils/envUtils'
import { ref, computed, watch, onMounted } from 'vue'
const { t } = i18n.global
interface Tab {
  value: string
  label: string
}

interface DemoConfig {
  title: string
  typeLabel: string
  principle: string
  tabs?: Tab[]
  features: string[]
  description: string[]
  videoFiles: { [key: string]: string }
  defaultVideo: string
}

interface Props {
  type: SetType
}

interface Emits {
  (e: 'close'): void
  (e: 'confirm'): void
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<Emits>()
const brand = ref(Brand.irok)
const isKMEModeSupported = ref(false)
const activeTab = ref('normal')

// 根据 SetType 配置不同的演示内�?
const demoConfigs: Record<SetType, DemoConfig> = {
  [SetType.routine]: {
    title: '常规按键演示',
    typeLabel: '',
    principle: '',
    features: [],
    description: [],
    videoFiles: {},
    defaultVideo: '',
  },
  [SetType.dks]: {
    title: t('DKS_title'),
    typeLabel: 'DKS',
    principle: t('DKS_principle'),
    features: [t('DKS_feature_1'), t('DKS_feature_2'), t('DKS_feature_3')],
    description: [t('DSKS_description')],
    videoFiles: { default: 'dks_demo.webp' },
    defaultVideo: 'dks_demo.webp',
  },
  [SetType.mt]: {
    title: t('MT_title'),
    typeLabel: 'MT',
    principle: t('MT_principle'),
    features: [t('MT_feature_1'), t('MT_feature_2')],
    description: [t('MT_description')],
    videoFiles: { default: 'mt_demo.webp' },
    defaultVideo: 'mt_demo.webp',
  },
  [SetType.tgl]: {
    title: t('TGL_title'),
    typeLabel: 'TGL',
    principle: t('TGL_principle'),
    features: [t('TGL_feature_1'), t('TGL_feature_2')],
    description: [t('TGL_description')],
    videoFiles: { default: 'tgl_demo.webp' },
    defaultVideo: 'tgl_demo.webp',
  },
  [SetType.mpt]: {
    title: t('MPT_title'),
    typeLabel: 'MPT',
    principle: t('MPT_principle'),
    features: [t('MPT_feature_1'), t('MPT_feature_2'), t('MPT_feature_3')],
    description: [t('MPT_description')],
    videoFiles: { default: 'mpt_demo.webp' },
    defaultVideo: 'mpt_demo.webp',
  },
  [SetType.end]: {
    title: t('END_title'),
    typeLabel: 'END',
    principle: t('END_principle'),
    features: [t('END_feature_1'), t('END_feature_2'), t('END_feature_3'), t('END_feature_4')],
    description: [t('END_description')],
    videoFiles: { default: 'end_demo.webp' },
    defaultVideo: 'end_demo.webp',
  },
  [SetType.kme]: {
    title: t('KME_title'),
    typeLabel: 'SOCD',
    principle: t('KME_principle'),
    tabs: [
      { value: 'normal', label: t('KME_tab_normal') },
      { value: 'a_priority', label: t('KME_tab_a_priority') },
      { value: 'b_priority', label: t('KME_tab_b_priority') },
      { value: 'neutral', label: t('KME_tab_neutral') },
    ],
    features: [t('KME_feature_1'), t('KME_feature_2'), t('KME_feature_3'), t('KME_feature_4')],
    description: [t('KME_description')],
    videoFiles: {
      normal: 'socd_last_input_demo.webp',
      a_priority: 'socd_a_priority_demo.webp',
      b_priority: 'socd_b_priority_demo.webp',
      neutral: 'socd_neutral_demo.webp',
      default: 'socd_a_priority_demo.webp',
    },
    defaultVideo: 'socd_a_priority_demo.webp',
  },
  [SetType.rs]: {
    title: t('RS_title'),
    principle: t('RS_principle'),
    typeLabel: 'RS',
    features: [t('RS_feature_1'), t('RS_feature_2')],
    description: [t('RS_description')],
    videoFiles: { default: 'rs_demo.webp' },
    defaultVideo: 'rs_demo.webp',
  },
  [SetType.macro]: {
    title: '',
    principle: '',
    typeLabel: '',
    features: [],
    description: [],
    videoFiles: {},
    defaultVideo: '',
  },
}

// 当前配置
const demoConfig = computed(() => demoConfigs[props.type] || demoConfigs[SetType.routine])

// 当前视频
const currentVideoSrc = computed(() => {
  const config = demoConfig.value
  const isCarotmas = brand.value == Brand.carotmas
  if (config.tabs && activeTab.value in config.videoFiles) {
    if (isCarotmas) {
      return isElectron() ? new URL(`../../assets/webp_files/carotmas/${config.videoFiles[activeTab.value]}`, import.meta.url).href : new URL(`/src/assets/webp_files/carotmas/${config.videoFiles[activeTab.value]}`, import.meta.url).href
    }
    return isElectron() ? new URL(`../../assets/webp_files/${config.videoFiles[activeTab.value]}`, import.meta.url).href : new URL(`/src/assets/webp_files/${config.videoFiles[activeTab.value]}`, import.meta.url).href
  }
  if (isCarotmas) {
    return isElectron() ? new URL(`../../assets/webp_files/carotmas/${config.defaultVideo}`, import.meta.url).href : new URL(`/src/assets/webp_files/carotmas/${config.defaultVideo}`, import.meta.url).href
  }
  return isElectron() ? new URL(`../../assets/webp_files/${config.defaultVideo}`, import.meta.url).href : new URL(`/src/assets/webp_files/${config.defaultVideo}`, import.meta.url).href
})

watch(
  () => props.type,
  () => {
    if (demoConfig.value.tabs && demoConfig.value.tabs.length > 0) {
      activeTab.value = demoConfig.value.tabs[0].value
    } else {
      activeTab.value = 'normal'
    }
  },
  { immediate: true },
)

const close = () => {
  emit('close')
}

const confirm = () => {
  emit('confirm')
  close()
}

onMounted(async () => {
  isKMEModeSupported.value = await DeviceCapability.isKMEModeSupported()
  brand.value = await getBrand()
})
</script>

<style scoped lang="less">
.set-action-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--glass-overlay-bg);
  backdrop-filter: var(--glass-overlay-blur);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px; /* 缩小边距 */
}

.set-action-container {
  width: 836px;
  max-width: 95%;
  max-height: calc(100vh - 80px); /* 限制最高高度 */
  background: var(--color-white);
  position: relative;
  padding: 32px; /* 稍微缩小内边距 */
  border-radius: var(--radius-l);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column; /* 垂直排布以支持内部滚动 */
  animation: feedbackFadeIn 0.4s var(--transition-base);
}

.close_btn_pos {
  position: absolute;
  top: 16px;
  right: 23px;
  z-index: 10;
}

.title {
  text-align: center;
  margin: 0 0 20px 0;
  font-size: 22px; /* 稍微缩小字号 */
  font-weight: bold;
  color: var(--color-dark);
  flex-shrink: 0; /* 保持标题不被压缩 */
}

.content {
  display: flex;
  gap: 32px;
  background: rgba(var(--color-brand-2-rgb), 0.03);
  padding: 24px; /* 显著缩小内部边距 */
  border-radius: var(--radius-m);
  overflow-y: auto; /* 核心修改：允许内部滚动 */
  flex: 1; /* 占据剩余空间 */
  min-height: 0; /* 允许 flex 项目缩小 */

  &::-webkit-scrollbar {
    display: none; /* 隐藏滚动条 */
  }
  scrollbar-width: none;
}

.left-section {
  width: 294px;
  flex-shrink: 0; /* 禁止左侧内容缩窄 */
}

/* ... existing styles ... */
.section-title {
  font-size: 16px;
  color: var(--brand-color-pure);
  margin-bottom: 12px;
  font-weight: bold;
}

.function-options {
  margin-bottom: 14px;
}

.tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tab {
  padding: 8px 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: var(--color-white);
  border-radius: var(--radius-s);
  cursor: pointer;
  font-size: 12px;
  color: var(--color-text-hint);
  transition: var(--transition-base);

  &.active {
    background: var(--brand-color-pure);
    color: var(--color-white);
    border-color: var(--brand-color-pure);
    box-shadow: 0 4px 12px rgba(var(--color-brand-2-rgb), 0.2);
  }

  &:hover:not(.active) {
    border-color: var(--brand-color-pure);
    color: var(--brand-color-pure);
    transform: translateY(-2px);
  }
}
.function-principle {
  margin-bottom: 30px;
  .principle {
    font-size: 14px;
    color: var(--color-text-hint);
    line-height: 1.5;
  }
}

.core-features {
  margin-bottom: 30px;
}

.feature-list {
  margin: 0;
  padding-left: 16px;
  font-size: 14px;
  color: var(--color-text-hint);
  line-height: 1.6;

  li {
    margin-bottom: 4px;
  }
}

.description {
  font-size: 14px;
  color: var(--brand-color-pure);
  line-height: 1.5;

  p {
    margin: 0 0 4px 0;
  }
}

.demo-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.demo-container {
  background: var(--color-white);
  border-radius: var(--radius-s);
  height: 225px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.video-demo-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
}

.demo-video,
.demo-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: var(--radius-s);
  object-fit: contain;
}

.demo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  color: var(--color-text-hint);

  .placeholder-icon {
    font-size: 32px;
    margin-bottom: 8px;
  }

  .placeholder-text {
    font-size: 12px;
  }
}

.key-demo-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.footer {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.confirm-btn {
  background: var(--brand-color);
  color: var(--color-white);
  border: none;
  padding: 10px 32px;
  border-radius: var(--radius-s);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--action-btn-primary-shadow);
  transition: var(--transition-base);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--action-btn-primary-shadow-hover);
  }

  &:active {
    transform: translateY(0);
  }
}

@keyframes feedbackFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
